import React from 'react';
import Card from 'components/Card';
import Typography from 'components/Typography/Typography';
import { Loader } from 'components';
import { formatDate, stringOrLoadingText } from 'common/helpers/v1';
import { MaintenanceEventStatus } from 'types/maintenance';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { IcoLoop } from 'icons/indexed-icons/IcoLoop';
import { Table } from '..';
import theme from 'themes';

interface MaintenanceEventDetailCardProps {
  description?: string | undefined;
  createdAt?: Date | undefined;
  machineDescription?: string | undefined;
  subComponent?: string | undefined;
  status?: MaintenanceEventStatus | undefined;
  suggestedDue?: Date | undefined;
  isLoading: boolean;
  children?: JSX.Element;
}

export const CustomCard = styled(Card)`
  border: 1px solid ${theme.colors.tableborder};
  border-radius: 4px;
  padding: 0.75rem 0;
`;
export const CardFooter = styled.div<{ textAlign?: string }>`
  margin: 0 0.75rem;
  border-top: 1px solid #0000002e;
  padding: 0.75rem 0 0;
  text-align: ${({ textAlign }) => textAlign};
`;

const MaintenanceEventDetailCard = ({
  createdAt,
  machineDescription,
  subComponent,
  status,
  suggestedDue,
  isLoading,
  children
}: MaintenanceEventDetailCardProps): JSX.Element => {
  const { t } = useTranslation(['fpns']);
  return (
    <CustomCard>
      <Card.Header padding="0">
        <Typography
          style={{
            display: 'flex',
            gap: '0.5rem',
            alignItems: 'center',
            marginLeft: '.75rem',
            marginBottom: '.5rem',
            fontSize: '0.875rem',
            fontWeight: '600'
          }}
        >
          <IcoLoop />
          {stringOrLoadingText(
            machineDescription,
            isLoading,
            t('machine', { ns: 'common' }) as string
          )}
        </Typography>
      </Card.Header>
      <Card.Body pl={'3rem'} pt={0}>
        {isLoading && <Loader size={40} />}
        {!isLoading && (
          <Table col={2}>
            <tbody>
              <tr>
                <td>
                  <Typography
                    as="span"
                    size="0.75rem"
                    weight="semi-bold"
                    color={theme.colors.lightGrey8}
                  >
                    {t('created')}:
                  </Typography>
                </td>
                <td>
                  <Typography
                    as="span"
                    size="0.75rem"
                    weight="semi-bold"
                    color={theme.colors.lightGrey8}
                  >
                    {createdAt ? formatDate(createdAt, 'short') : t('creation_date_unavailable')}
                  </Typography>
                </td>
              </tr>
              <tr>
                <td>
                  <Typography
                    as="span"
                    size="0.75rem"
                    weight="semi-bold"
                    color={theme.colors.lightGrey8}
                  >
                    {t('sub_component', { ns: 'common' })}:
                  </Typography>
                </td>
                <td>
                  <Typography
                    as="span"
                    size="0.75rem"
                    weight="semi-bold"
                    color={theme.colors.lightGrey8}
                  >
                    {stringOrLoadingText(
                      subComponent,
                      isLoading,
                      t('sub_component', { ns: 'common' }) as string
                    )}
                  </Typography>
                </td>
              </tr>
              <tr>
                <td>
                  <Typography
                    as="span"
                    size="0.75rem"
                    weight="semi-bold"
                    color={theme.colors.lightGrey8}
                  >
                    {t('status', { ns: 'common' })}:
                  </Typography>
                </td>
                <td>
                  <Typography
                    as="span"
                    size="0.75rem"
                    weight="semi-bold"
                    color={theme.colors.lightGrey8}
                  >
                    {status ? t(status) : t('maintenance_event_status_unavailable')}
                  </Typography>{' '}
                  <Typography
                    as="span"
                    size="0.75rem"
                    weight="semi-bold"
                    color={theme.colors.lightGrey8}
                  >
                    {suggestedDue != undefined
                      ? formatDate(suggestedDue, 'short')
                      : t('suggested_due_date_unavailable')}
                  </Typography>
                </td>
              </tr>
            </tbody>
          </Table>
        )}
      </Card.Body>
      <CardFooter>{children}</CardFooter>
    </CustomCard>
  );
};

export default MaintenanceEventDetailCard;
